import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import {
  SellerOrder,
  SellerOrderConnection,
} from '@shopify-multiseller/graphql/partner/types';
import { SectionCard } from '../../components/SectionCard';
import { EmptyState } from '../../components/EmptyState';
import { SellerOrderStatusBadge } from '../../components/StatusBadge/SellerOrderStatusBadge';
import { ORDERS_QUERY } from '../SellerOrders/queries';

type OrdersData = { sellerOrderConnection: SellerOrderConnection };

/**
 * "1 product · 2 items", "3 products · 6 items".
 *
 * A row that says only "#1094" makes you open the order to learn anything, so
 * it says what is in it. It used to name the first product and count the rest —
 * "Aurora Lamp ×2 +1" — which is the grammar review rejected on the triage
 * queue: "the left side makes no sense as it lists specific items, whereas an
 * order can contain multiple items." Same objection, same answer, so the two
 * lists read the same way.
 */
const contents = (order: SellerOrder): string => {
  const items = order.items ?? [];
  if (items.length === 0) return 'No items';
  const units = items.reduce((sum, item) => sum + (item?.quantity ?? 0), 0);
  return `${items.length} ${items.length === 1 ? 'product' : 'products'} · ${units} ${
    units === 1 ? 'item' : 'items'
  }`;
};

/**
 * The last few orders, on the screen sellers open first.
 *
 * Checking whether anything new came in is the most common reason to open the
 * dashboard, and it previously required a trip to the orders list to find out.
 * Reuses the orders query rather than adding an endpoint.
 */
export const RecentOrders: React.FC = () => {
  const { data, loading, error } = useQuery<OrdersData>(ORDERS_QUERY, {
    variables: { first: 5, filter: '' },
    fetchPolicy: 'cache-and-network',
  });

  const rows: SellerOrder[] = (data?.sellerOrderConnection.edges ?? [])
    .map((edge) => edge?.node as SellerOrder | undefined)
    .filter((node): node is SellerOrder => !!node);

  return (
    <SectionCard
      title="Recent orders"
      testId="dashboard-recent-orders"
      trailing={
        <Link to="/orders" className="text-primary hover:underline">
          All orders
        </Link>
      }
      flush
    >
      {loading && rows.length === 0 && (
        <div className="space-y-2 px-5 py-4">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-9 animate-pulse rounded bg-muted" />
          ))}
        </div>
      )}

      {/* A list that failed to load is not a list with nothing in it — saying
          "no orders yet" to a seller who has orders is worse than saying
          nothing. */}
      {!loading && error && (
        <p className="px-5 py-4 text-sm text-muted-foreground">
          These didn&apos;t load. Reload the page to try again.
        </p>
      )}

      {!loading && !error && rows.length === 0 && (
        <EmptyState
          size="sm"
          icon={Inbox}
          title="No orders yet"
          description="They appear here the moment a customer buys one of your live products."
        />
      )}

      {rows.map((order) => (
        <Link
          key={order.id}
          to={`/orders/${order.id}`}
          data-testid="dashboard-recent-order"
          className="flex items-center gap-3 border-b border-border/60 px-5 py-2.5 last:border-b-0 hover:bg-muted/50"
        >
          {/* What was ordered, on the same line as the number: a date and a
              total say nothing you can act on, and both are on the order. */}
          {/* One text node per part, not `#{alias}` and a separate `{' · '}`:
              JSX splits those into three nodes, so "#1094" and "· Juniper
              Throw ×1" exist on screen but not as readable strings. */}
          <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{`#${order.idAlias}`}</span>
            {` · ${contents(order)}`}
          </span>
          {order.status && <SellerOrderStatusBadge status={order.status} />}
        </Link>
      ))}
    </SectionCard>
  );
};
